import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { ButtonTabNavHorizontal, ListingCard } from '../../../../components';
import Field, { hasDataInFields } from '../../Field';
import { useConfiguration } from '../../../../context/configurationContext';

import SectionContainer from '../SectionContainer';
import { fetchListingTab, listingTabDataSelector } from '../../PageBuilder.duck';

import css from './SectionListingTabs.module.css';

const SectionListingTabs = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    appearance,
    blocks,
    options,
  } = props;

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const config = useConfiguration();
  const dispatch = useDispatch();

  const tabs = useMemo(
    () =>
      blocks.map(({ callToAction, title }, index) => {
        const { content: text } = title || {};
        const { href } = callToAction || {};

        return {
          text,
          callToAction,
          selected: selectedTabIndex === index,
          onClick: () => {
            setSelectedTabIndex(index);
            dispatch(fetchListingTab(config, href));
          },
        };
      }),
    [blocks, selectedTabIndex, config]
  );

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasTitle = hasDataInFields([title], fieldOptions);
  const { callToAction } = tabs[selectedTabIndex] || {};
  const hasCta = hasDataInFields([callToAction], fieldOptions);
  const hasTabs = tabs.length > 1;
  const { data: listings, inProgress } = useSelector(listingTabDataSelector(callToAction?.href));
  const hasListings = !inProgress && listings.length;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      <header className={css.sectionDetails}>
        {hasTitle && (
          <Field
            className={classNames(defaultClasses.title, css.title)}
            options={fieldOptions}
            data={title}
          />
        )}
        {hasTabs && (
          <ButtonTabNavHorizontal className={css.tabs} tabClassName={css.tab} tabs={tabs} />
        )}
      </header>
      <div className={css.blockContainer}>
        {inProgress && <FormattedMessage id="SectionListingTab.loading" />}
        {!hasListings && <FormattedMessage id="SectionListingTab.noResults" />}
        {listings.map(listing => (
          <ListingCard key={listing.id.uuid} listing={listing} />
        ))}
      </div>
      {hasCta && (
        <div className={css.ctaContainer}>
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </div>
      )}
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionListingTabs.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  isInsideContainer: false,
  options: null,
};

SectionListingTabs.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  isInsideContainer: bool,
  options: propTypeOption,
};

export default SectionListingTabs;
